'use client';

import type { CSSProperties, ReactNode } from 'react';

type SettingsContentPaneProps = {
  /**
   * Extra legacy shell classes the page needs on top of the standard pair —
   * Vendor Preferences adds `has-tabs vp-page-content`.
   */
  shellClassName?: string;
  /**
   * Keep the pane exactly one viewport tall instead of letting it grow with its
   * content.
   *
   * Defaults to on for a caller that brings its own `shellClassName`, because
   * the one such caller today declares its own bounded geometry on this very
   * element: `.vp-page-content` is `display: flex; flex-direction: column;
   * height: 100dvh; overflow: hidden` (src/scss/pages/_vendor-prioritization.scss:18),
   * which is what lets Vendor Preferences' category list and detail scroll
   * independently. The growth utilities below are important, so they would
   * outrank that rule and crush its flex children to zero height. Pass `false`
   * explicitly if a page needs both a shell class and a surface that grows.
   *
   * MIND THE PROSE IN THIS FILE — and do not write a bare utility name in it.
   * Tailwind's scan of `src/components/DS` is textual and cannot tell a comment
   * from code, so a utility name written as an ENGLISH WORD here generates a real
   * rule. If a legacy component class happens to share that name, the rule lands
   * on legacy markup that no stylesheet defends. One word in an earlier draft of
   * this docblock generated a `visibility` utility whose selector is also the
   * app's Bootstrap sidebar wrapper class, and hid the sidebar on every page of
   * the app. Suffixed forms are safe; the scan matches whole tokens.
   * `tests/unit/Components/DS/KitUtilityGeneration.test.ts` now fails on any
   * generated class that shares a name with a legacy one.
   */
  bounded?: boolean;
  /**
   * Inline layout, for the fixed-header top offset a page measures at runtime.
   * It has to be an inline style: the value is a measurement, not a token.
   */
  style?: CSSProperties;
  children: ReactNode;
};

/**
 * What makes the white surface reach the bottom of a page taller than the
 * viewport.
 *
 * `.ls-data-content` is `height: 100%` — `@include square(100%)` in
 * src/scss/layouts/_skeleton.scss:46, resolving against `.ls-data`'s `height: 100%`
 * inside the `100dvh` shell. So the pane's BOX is exactly one viewport tall
 * however long its content is, and because nothing clips, the content below that
 * edge renders straight onto `.ls`'s `--fill-gray-lightest` (#f2f4f7,
 * _skeleton.scss:16). Measured on Automations -> Notifications & Messages: box
 * 970px, scrollHeight 1532px, everything past the Quiet hours row on gray.
 *
 * `min-h-full` keeps the short-page behaviour that the height was there for — the
 * pane still fills the viewport, so no gray shows under a short tab — while
 * `h-auto` lets a long one grow. Neither introduces an `overflow`, so `.ls-data`
 * remains the scrollport `SettingsSaveBar`'s `sticky bottom-0` resolves against.
 *
 * ONLY `h-auto` CARRIES THE `!`, and the asymmetry is deliberate. It has to beat
 * an UNLAYERED `height` on this element (two of them: `.ls-data-content` above,
 * and `.ls-overlay + .ls-data-content--multi { height: 100vh }` at
 * _skeleton.scss:116), and unlayered normal beats layered normal however specific
 * — the same trap `bg-bg-surface!` documents below. Nothing unlayered sets
 * `min-height` on this element, so the plain utility is enough there and an
 * unearned `!` would only obscure which one is load-bearing.
 *
 * Bootstrap ships its own unlayered `.h-auto { height: auto !important }`, and it
 * is NOT what is doing the work here: the important modifier is part of the class
 * NAME, so this element carries `h-auto!` and matches `.h-auto\!` only. Verified
 * against a compile — `.h-auto\! { height: auto !important }` in the DS bundle,
 * `.h-auto { height: auto !important }` in the app's, two different selectors and
 * no collision for check:ds-collisions to record.
 */
const GROWS_WITH_CONTENT = 'h-auto! min-h-full';

export const SETTINGS_GRID_LAYOUT = 'grid! grid-rows-[1fr_auto] pb-0!';

/**
 * The content pane a settings page renders into.
 *
 * Two jobs, and it exists because they cannot be done anywhere else:
 *
 * 1. THE SURFACE COLOUR. The design puts settings content on white
 *    (`color/bg/surface` #ffffff); the app's shared `.ls-data-content` paints it
 *    `--fill-gray-lightest`. That SCSS is used by many non-settings pages and
 *    must not change, so the settings pane overrides it here with the DS's own
 *    `bg-bg-surface` token. Putting the colour on `SettingsPage` instead would
 *    leave a gray band under a short page, because that column is only as tall
 *    as its content — the pane is the element that fills the viewport.
 *
 *    THE TRAILING `!` IS LOAD-BEARING. `.ls-data-content`'s background is an
 *    UNLAYERED declaration on this very element, and unlayered normal beats
 *    layered normal however specific the layered one is — so the plain utility
 *    lost and the pane measured rgb(242,244,247). Tailwind's important modifier
 *    puts the declaration in `@layer utilities` AS IMPORTANT, and importance is
 *    weighed before layers, so it wins. Section 4 of design-system-compat.css is
 *    the doctrine; this is the same remedy ListingTable uses.
 *
 * 2. TAILWIND EXTRACTION. `src/app/design-system.css` pins the scan to
 *    `src/components/DS/**\/*.tsx`, so a utility written in a view file is never
 *    generated and silently does nothing. The settings chrome IS a view file.
 *    That is the whole reason this wrapper is a kit component rather than a
 *    className on the div in SettingsChrome.
 *
 * The legacy classes stay: `.ls-data-content` supplies the pane's padding and
 * `--multi` its fixed-header offset, both shared with the rest of the app. Its
 * height is the one part of that geometry we override — see
 * `GROWS_WITH_CONTENT`. The paint is ours.
 */
export const SettingsContentPane = ({
  shellClassName,
  bounded = shellClassName != null,
  style,
  children,
}: SettingsContentPaneProps) => (
  <div
    data-lula-ds=""
    // The space before the interpolation is load-bearing; see the note in
    // SettingsRow. Tailwind reads this file as text, and a utility written flush
    // against `${` is never extracted, so the class is never generated.
    className={`ls-data-content ls-data-content--multi bg-bg-surface! ${
      bounded ? '' : GROWS_WITH_CONTENT
    } ${shellClassName ?? ''}`}
    data-settings-content-pane=""
    style={style}
  >
    {children}
  </div>
);

export default SettingsContentPane;
